import React, { KeyboardEvent, ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { XIcon } from '@heroicons/react/solid';
import BtButton from '../bt-button/bt-button';
import Spinner from '../../components/shared/Spinner/Spinner';
import { ErrorMessageIcon } from '../../components/shared/ErrorMessageIcon/ErrorMessageIcon';
import WarningOutlineIcon from '../../components/svgs/warningOutlineIcon';
import { SearchIcon } from '../../components/svgs';
import { CompanySearchRow } from '../../services/service-company-search';
import { highlight, townName } from './display';
import styles from './app-company-search.module.scss';

// The proxy refuses longer queries.
const MAX_QUERY_LENGTH = 200;

export type Found = { rows: CompanySearchRow[]; truncated: boolean };

type Props = {
  inputId: string;
  listId: string;
  inputRef?: RefObject<HTMLInputElement>;
  changeRef?: RefObject<HTMLDivElement>;
  label?: ReactNode;
  required?: boolean;
  errorMessage?: string;
  prefix?: ReactNode;
  selectedAction?: ReactNode;
  cardClassName?: string;
  query: string;
  loading: boolean;
  rateLimited: boolean;
  found: Found | null;
  open: boolean;
  activeIndex: number;
  selected: CompanySearchRow | null;
  onQueryChange: (query: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onPick: (company: CompanySearchRow) => void;
  onClear: () => void;
  onChangePick: () => void;
};

const optionId = (listId: string, index: number) => `${listId}-option-${index}`;
const idList = (...ids: (string | false)[]) => ids.filter(Boolean).join(' ') || undefined;

export default function CompanySearchView({
  inputId,
  listId,
  inputRef,
  changeRef,
  label,
  required,
  errorMessage,
  prefix,
  selectedAction,
  cardClassName = '',
  query,
  loading,
  rateLimited,
  found,
  open,
  activeIndex,
  selected,
  onQueryChange,
  onKeyDown,
  onFocus,
  onBlur,
  onPick,
  onClear,
  onChangePick,
}: Props) {
  const [t] = useTranslation('companySearch');
  const rows = found?.rows ?? [];
  const listOpen = open && rows.length > 0;
  const noMatch = open && found !== null && rows.length === 0;
  const showHint = !loading && (rateLimited || !found);

  const labelId = `${inputId}-label`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const labelContent = label && (
    <>
      {label}
      {required && (
        <span className={styles.required} aria-hidden="true">
          *
        </span>
      )}
    </>
  );
  const errorText = errorMessage && (
    <span id={errorId} className={styles.srOnly}>
      {errorMessage}
    </span>
  );

  if (selected) {
    const nameId = `${inputId}-name`;
    const numberId = `${inputId}-number`;
    return (
      <div className={styles.root}>
        {labelContent && (
          <span id={labelId} className={styles.label}>
            {labelContent}
          </span>
        )}
        <div
          role="group"
          aria-labelledby={labelContent ? labelId : undefined}
          className={`${styles.card} ${cardClassName} ${errorMessage ? styles.error : ''}`}
        >
          <div className={styles.cardText}>
            <span id={nameId} className={styles.cardName}>
              {selected.name}
            </span>
            <span id={numberId} className={styles.orgNumber}>
              {selected.orgNumber}
            </span>
          </div>
          {errorMessage && <ErrorMessageIcon errorMessage={errorMessage} />}
          {selectedAction}
          <BtButton
            ref={changeRef}
            color="white"
            size="xxs"
            tabIndex={0}
            ariaDescribedBy={idList(nameId, numberId, !!errorMessage && errorId)}
            onClick={onChangePick}
          >
            {t('selected.change', 'Ändra')}
          </BtButton>
        </div>
        {errorText}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {labelContent && (
        <label id={labelId} className={styles.label} htmlFor={inputId}>
          {labelContent}
        </label>
      )}
      <div className={`${styles.box} ${errorMessage ? styles.error : ''}`}>
        {prefix}
        <span className={styles.searchIcon} aria-hidden="true">
          <SearchIcon size={18} />
        </span>
        <input
          ref={inputRef}
          id={inputId}
          className={styles.input}
          type="text"
          role="combobox"
          autoComplete="off"
          maxLength={MAX_QUERY_LENGTH}
          value={query}
          placeholder={t('field.placeholder', 'Företagsnamn eller org.nr')}
          aria-autocomplete="list"
          aria-controls={listOpen ? listId : undefined}
          aria-expanded={listOpen}
          aria-activedescendant={listOpen ? optionId(listId, activeIndex) : undefined}
          aria-required={required}
          aria-invalid={!!errorMessage}
          aria-describedby={idList(!!errorMessage && errorId, showHint && hintId)}
          onChange={event => onQueryChange(event.target.value)}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {loading && (
          <span className={styles.trailing}>
            <Spinner size="xxs" progress={0} />
          </span>
        )}
        {!loading && query && (
          <button
            type="button"
            className={styles.clear}
            aria-label={t('field.clear', 'Rensa')}
            onMouseDown={event => event.preventDefault()}
            onClick={onClear}
          >
            <XIcon className={styles.clearIcon} aria-hidden="true" />
          </button>
        )}
        {errorMessage && (
          <span className={styles.trailing}>
            <ErrorMessageIcon errorMessage={errorMessage} />
          </span>
        )}
      </div>
      {errorText}

      {listOpen && (
        // Otherwise the input blurs on mousedown and the list closes before a row's click lands.
        <div className={styles.menu} onMouseDown={event => event.preventDefault()}>
          <ul
            id={listId}
            role="listbox"
            aria-labelledby={labelContent ? labelId : undefined}
            className={styles.listbox}
          >
            {rows.map((row, index) => (
              <li
                key={row.orgNumber}
                id={optionId(listId, index)}
                role="option"
                aria-selected={index === activeIndex}
                className={`${styles.option} ${index === activeIndex ? styles.active : ''}`}
                onClick={() => onPick(row)}
              >
                <span className={styles.optionText}>
                  <span className={styles.optionName}>
                    {highlight(row.name, query).map((part, partIndex) =>
                      part.match ? (
                        <mark key={partIndex} className={styles.mark}>
                          {part.text}
                        </mark>
                      ) : (
                        <React.Fragment key={partIndex}>{part.text}</React.Fragment>
                      ),
                    )}
                  </span>
                  <span className={styles.optionTown}>{townName(row.city)}</span>
                </span>
                <span className={styles.orgNumber}>{row.orgNumber}</span>
              </li>
            ))}
          </ul>
          {found?.truncated && (
            <p className={styles.footer}>
              {t('status.keepTyping', 'Fortsätt skriva för att förfina listan.')}
            </p>
          )}
        </div>
      )}

      {noMatch && (
        <div className={styles.noMatch}>
          <span className={styles.noMatchTitle}>
            {t('noMatch.title', 'Inget företag matchar det namnet eller numret.')}
          </span>
          <span className={styles.noMatchHint}>
            {t(
              'noMatch.hint',
              'Kontrollera stavningen eller sök på organisationsnummer i stället. Ett företag som inte finns i registret kan inte läggas till här.',
            )}
          </span>
        </div>
      )}

      {!listOpen && !noMatch && loading && (
        <div className={styles.hint}>{t('status.searching', 'Söker…')}</div>
      )}

      {showHint && (
        <div id={hintId} className={styles.hint}>
          {rateLimited
            ? t(
                'status.rateLimited',
                'För många sökningar just nu. Vänta en stund och försök igen.',
              )
            : t('field.hint', 'Sök i företagsregistret på namn eller organisationsnummer.')}
        </div>
      )}
    </div>
  );
}

export function CompanySearchUnavailable() {
  const [t] = useTranslation('companySearch');
  return (
    <p role="alert" className={styles.unavailable}>
      <span className={styles.unavailableIcon} aria-hidden="true">
        <WarningOutlineIcon />
      </span>
      <span>
        {t(
          'unavailable.message',
          'Företagssökningen är inte tillgänglig just nu. Fyll i organisationsnummer och företagsnamn manuellt.',
        )}
      </span>
    </p>
  );
}
